<template>
  <section
    class="summary-card"
    :class="[summaryCardClass, themeClass, paddingsClass]"
  >
    <div
      v-for="item in vansInformation"
      :key="item.value"
      class="summary-card__item"
      :class="summaryCardItemClass(item.key)"
    >
      <wb-icon :name="item.icon" v-if="isPrincipal" />
      {{ item.key }}
      <span class="summary-card__item__value" :class="{ hasKey: item.key }">{{
        item.value
      }}</span>
    </div>
    <div
      v-for="(item, index) in summaryInformation"
      :key="item.value"
      class="summary-card__item"
      :class="summaryCardItemClass(item.key)"
    >
      <wb-icon :name="item.icon" v-if="isPrincipal" />
      <span class="summary-card__item__key" :class="summaryItemKeyClass"
        >{{ item.key
        }}<sup v-if="item.showFootNotes" class="summary-card__item__sup">{{
          footNoteReference ? footNoteReference : `[${index + 1}]`
        }}</sup></span
      >
      <span
        class="summary-card__item__value"
        :class="summaryItemValueClass(item.key)"
        >{{ item.value }}</span
      >
      <wb-tooltip-host
        class="form-section-hint"
        position="top"
        v-if="showWeightNotificationTooltip && item.tooltipText"
      >
        <wb-tooltip>{{ item.tooltipText }} </wb-tooltip>
        <wb-icon :name="item.icon" v-if="item.icon" />
      </wb-tooltip-host>
      <wb-icon :name="item.icon" v-else-if="item.icon" />
    </div>
  </section>
</template>
<script lang="ts">
import { computed, defineComponent } from 'vue';

import { useDirection } from '../../composables/direction.composable';

import GenericReceivedObject from '../../models/utilities/generic-received-object.model';
import { TestDriveStatus } from '../../models/utilities/testdrive-status.enum';

import { useStore } from '../../composables/store.composable';
import UtilsService from '../../services/utils.service';
import { CONTEXT_OPTIONS, ROUTER } from '../../../../otbc/src/models/constants';
import { useMediaQuery } from '../../../src/composables/media-query.composable';

export default defineComponent({
  name: 'otb-summary-card',
  props: {
    disabled: Boolean,
    summaryInformation: {
      type: Array as () => GenericReceivedObject[],
    },
    type: {
      type: String as () => 'principal' | 'details',
      default: 'principal',
    },
    status: {
      type: String as () => TestDriveStatus,
      default: TestDriveStatus.BOOKED,
    },
    theme: {
      type: String,
    },
    paddings: {
      type: String,
    },
    path: {
      type: String,
    },
  },
  setup(props) {
    const { isRTL } = useDirection();
    const { state } = useStore();
    const { isMobile, isMq3 } = useMediaQuery();
    const isPrincipal = computed((): boolean => {
      return props.type === 'principal';
    });

    const showWeightNotificationTooltip = computed((): boolean => {
      return (
        props.path !== ROUTER.PERSONAL_DATA &&
        props.path !== ROUTER.REQUEST &&
        props.path !== ROUTER.SUMMARY
      );
    });

    const statusClass = computed((): string => {
      return UtilsService.getStatusClass(props.status);
    });

    const summaryItemKeyClass = computed((): string => {
      if (state.configuration.context === CONTEXT_OPTIONS.VANS) {
        return 'wb-type-copy-secondary summary-card__item__key-vans';
      }
      if (isMobile.value || isMq3.value)
        return 'wb-type-copy-secondary summary-card__item__key--pc';
      return 'wb-type-copy-tertiary summary-card__item__key--pc';
    });

    const summaryCardClass = computed((): string[] => {
      const typeClass = isPrincipal.value ? 'summary-card-principal' : '';
      const disabledClass = props.disabled ? 'summary-card--disabled' : '';
      return [typeClass, statusClass.value, disabledClass];
    });

    const summaryCardItemRTL = computed((): string => {
      return isRTL.value ? 'summary-card__item--rtl' : '';
    });

    const themeClass = computed((): string => {
      return props.theme === 'white' ? 'white-theme' : 'default-theme';
    });

    const paddingsClass = computed((): string => {
      return props.paddings === 'none' ? 'no-paddings' : 'default-paddings';
    });

    const footNoteReference = computed((): string => {
      return state.configuration.footNotes?.defaultReference;
    });

    const summaryCardItemClass = (key: string): string => {
      let summaryCardItemRTLClass = summaryCardItemRTL.value;
      let computedClass = 'wb-type-copy-tertiary';

      if (isPrincipal.value && key) {
        computedClass = 'wb-type-copy-strong';
      } else if (isPrincipal.value && !key) {
        computedClass = 'wb-type-copy-secondary';
      }

      return summaryCardItemRTLClass
        ? `${computedClass} ${summaryCardItemRTLClass}`
        : computedClass;
    };

    const summaryItemValueClass = (key) => {
      if (state.configuration.context === CONTEXT_OPTIONS.VANS) {
        return key ? 'hasKey wb-type-copy-secondary' : 'wb-type-copy-secondary';
      }
      return key ? 'hasKey' : '';
    };

    return {
      isRTL,
      themeClass,
      isPrincipal,
      statusClass,
      summaryCardClass,
      summaryCardItemRTL,
      summaryCardItemClass,
      summaryItemValueClass,
      paddingsClass,
      summaryItemKeyClass,
      showWeightNotificationTooltip,
      footNoteReference,
    };
  },
});
</script>
<style scoped src="./summary-card.scss" lang="scss"></style>
